import { Activity, useEffect, useEffectEvent, useState } from 'react'
import * as styles from './HeaderEmbed.module.scss'
import { EMBED_CONFIG } from './config'

type Props = {
	allowHomeEmbed?: boolean
	inFlow?: boolean
	targetSelector?: string
}
type Versio = 'noticia_lg' | 'noticia_md' | 'noticia_sm' | 'noticia_xsm' | 'home'
type EmbedTarget = 'perHome' | 'perNoticia1' | 'perNoticia2'

const VERSIONS: Array<{ value: Versio; label: string }> = [
	{ value: 'home', label: 'HOME' },
	{ value: 'noticia_lg', label: 'NOTÍCIA LG' },
	{ value: 'noticia_md', label: 'NOTÍCIA MD' },
	{ value: 'noticia_sm', label: 'NOTÍCIA SM' },
	{ value: 'noticia_xsm', label: 'NOTÍCIA XSM' },
]

const HeaderEmbed = ({ allowHomeEmbed = false, inFlow = false, targetSelector = '[id^="app-"]' }: Props) => {
	const [text, setText] = useState<string | null>(null)
	const [versioActiva, setVersioActiva] = useState<Versio>('noticia_md')

	const handleClick = (target: EmbedTarget): void => {
		const perHome = target === 'perHome'
		const targetLabel =
			target === 'perHome'
				? 'HOME'
				: target === 'perNoticia1'
					? 'NOTÍCIA (1r embed)'
					: 'NOTÍCIA (2n embed i posteriors)'
		const url = window.location.origin
		const path = window.location.pathname
		const projecte = path.split('/')[1]
		const query = new URLSearchParams(window.location.search)
		query.delete('preview')
		query.delete('embed-home')
		let datasets = ''
		query.forEach((value, key) => {
			datasets += ` data-${key}="${value}"`
		})
		const slug = query.get('slug')

		if (projecte === 'hemicicles' || projecte === 'widgets-eleccions') {
			const esHemicicles = projecte === 'hemicicles'
			const slugProj = esHemicicles ? `graficsweb/hemicicles/${slug}` : slug
			const urlBase = query.get('url-base') || 'https://eleccions.ara.cat'
			const id = esHemicicles ? `hemicicle_${slug}` : slugProj?.replaceAll('/', '_')

			const embed = `<iframe id="${id}" src="${urlBase}/${slugProj}" height="58px" loading="lazy"></iframe>
			${
				perHome
					? `<style>#${id}{width: 1px;min-width: 100%;margin-bottom: 20px;border: none;overflow: hidden;}
			@media only screen and (min-width: 768px) {#${id}{margin-bottom: 56px;}}</style>`
					: `<style>#${id}{width: 1px;min-width: 100%;border: none;overflow: hidden;}</style>`
			}
			<script type="text/javascript" src="${urlBase}/scripts/iframeResizer.min.js"></script>
			<script>document.addEventListener("DOMContentLoaded", function() { 
				document.getElementById("${id}").onload= function() { 
					iFrameResize({ log: false }, "#${id}");
				};
			});</script>`
			navigator.clipboard.writeText(embed)
			setText(`Embed copiat per ${targetLabel} al porta-retalls`)
			setTimeout(() => setText(null), 5000)
		} else {
			let embed = ''
			if (target === 'perNoticia2') {
				embed = `<div id="app-${projecte}${slug ? `-${slug}` : ''}" class="embed-${projecte}"${datasets}></div>`
			} else {
				embed = `<div id="app-${projecte}${slug ? `-${slug}` : ''}" class="embed-${projecte}"${datasets}${perHome ? ` data-embed-home="1"` : ''}></div>
	<link rel="stylesheet" href="${url}/projectes/${projecte}/dist/style.css">
	<script type="module" src="${url}/projectes/${projecte}/dist/index.js"></script>`
			}

			navigator.clipboard.writeText(embed)
			setText(`Embed copiat per ${targetLabel} al porta-retalls`)
			setTimeout(() => setText(null), 5000)
		}
	}

	const handleVersio = (novaVersio: Versio): void => {
		localStorage.setItem('headerEmbedVersio', novaVersio ?? 'noticia_md')
		setVersioActiva(novaVersio)
		const url = new URL(window.location.href)

		// Editr search params
		const searchParams = new URLSearchParams(url.search)
		if (novaVersio === 'home') {
			searchParams.set('embed-home', '1')
		} else {
			searchParams.delete('embed-home')
		}
		const search = searchParams.toString()
		window.location.search = search
	}

	const onVersio = useEffectEvent((versio: Versio) => {
		setVersioActiva(versio)
		const url = new URL(window.location.href)
		const searchParams = new URLSearchParams(url.search)
		if (versio === 'home' && searchParams.get('embed-home') !== '1') {
			searchParams.set('embed-home', '1')
			window.location.search = searchParams.toString()
		}
		const appElement = document.querySelector(targetSelector) as HTMLElement
		appElement.style.maxWidth =
			versio === 'home' && allowHomeEmbed ? EMBED_CONFIG.mida_home : EMBED_CONFIG[`mida_${versio}`]
		appElement.style.margin = inFlow ? '0 auto 40px' : '220px auto 40px'
		appElement.style.boxShadow = '0 0 12px rgba(0, 0, 0, 0.15)'
		appElement.style.backgroundColor = '#ffffff'
	})

	useEffect(() => {
		const versio = localStorage.getItem('headerEmbedVersio') as Versio
		document.body.style.backgroundColor = '#f4f6f8'
		if (!versio) {
			localStorage.setItem('headerEmbedVersio', 'noticia_md')
			onVersio('noticia_md')
		} else {
			onVersio(versio)
		}
	}, [])

	return (
		<div className={`${(styles as any).container}${inFlow ? ` ${(styles as any).inFlow}` : ''}`}>
			<div>
				<p>Copia el codi de l’embed a:</p>
				<Activity mode={allowHomeEmbed ? 'visible' : 'hidden'}>
					<button
						className={`ara-button${text !== null ? ' disabled' : ''}`}
						onClick={() => handleClick('perHome')}
					>
						<span>HOME</span>
					</button>
				</Activity>
				<button
					className={`ara-button${text !== null ? ' disabled' : ''}`}
					onClick={() => handleClick('perNoticia1')}
				>
					<span>
						NOTÍCIA <span>(1r embed)</span>
					</span>
				</button>
				<button
					className={`ara-button${text !== null ? ' disabled' : ''}`}
					onClick={() => handleClick('perNoticia2')}
				>
					<span>
						NOTÍCIA <span>(2n embed i posteriors)</span>
					</span>
				</button>
			</div>
			<span style={{ opacity: text ? 1 : 0 }}>{text}</span>

			<div className={(styles as any).select}>
				{VERSIONS.map(({ value, label }) => {
					const radio = (
						<div key={value} onClick={() => handleVersio(value)}>
							<input
								type='radio'
								value={value}
								name='versio'
								checked={versioActiva === value}
								onChange={() => {}}
							/>
							<label htmlFor={value} style={{ fontWeight: versioActiva === value ? 700 : 400 }}>
								{label}
							</label>
						</div>
					)

					if (value === 'home') {
						return (
							<Activity key={value} mode={allowHomeEmbed ? 'visible' : 'hidden'}>
								{radio}
							</Activity>
						)
					}

					return radio
				})}
			</div>
		</div>
	)
}
export default HeaderEmbed
